import React, { createContext, useContext, useState, useEffect } from 'react';
import projectsService from '../../services/projectsService';
import { useAuth } from '../auth/AuthContext';

const ProjectContext = createContext();

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
};


export const ProjectProvider = ({ children }) => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load projects from localStorage as fallback
  const loadProjectsFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem('classtraks_projects');
      if (stored) {
        const parsedProjects = JSON.parse(stored);
        if (Array.isArray(parsedProjects) && parsedProjects.length > 0) {
          console.log('ProjectProvider - Loaded projects from localStorage:', parsedProjects);
          return parsedProjects;
        }
      }
    } catch (error) {
      console.error('Error loading projects from localStorage:', error);
    }
    return [];
  };

  // Fetch projects from database with localStorage fallback
  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ProjectProvider - Fetching projects from database...');
      const response = await projectsService.getProjects();
      console.log('ProjectProvider - Database response:', response);
      
      // If we got projects from database, use them
      if (response.projects && response.projects.length > 0) {
        console.log('ProjectProvider - Using database projects');
        setProjects(response.projects);
        return response.projects;
      }
      
      // If no database projects, check localStorage as fallback
      console.log('ProjectProvider - No database projects, checking localStorage...');
      const localProjects = loadProjectsFromLocalStorage();
      if (localProjects.length > 0) {
        console.log('ProjectProvider - Using localStorage projects as fallback');
        setProjects(localProjects);
        return localProjects;
      }
      
      // No projects found anywhere
      console.log('ProjectProvider - No projects found');
      setProjects([]);
      return [];
      
    } catch (error) {
      console.error('ProjectProvider - Error fetching from database:', error);
      setError(error.message);
      
      // Fallback to localStorage on database error
      console.log('ProjectProvider - Database error, falling back to localStorage...');
      const localProjects = loadProjectsFromLocalStorage();
      setProjects(localProjects);
      return localProjects;
    } finally {
      setLoading(false);
    }
  };

  // Load initial data and refetch when user changes
  useEffect(() => {
    if (user) {
      // Clear existing projects and refetch for new user
      setProjects([]);
      fetchProjects();
    }
  }, [user?.id]); // Refetch when user ID changes
    
  // Add new project to database
  const addProject = async (projectData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ProjectProvider - Adding project to database:', projectData);
      
      // Try to save to database
      try {
        const newProject = await projectsService.createProject(projectData);
        console.log('ProjectProvider - Project created in database:', newProject);
        
        // Add to local state
        setProjects(prev => [newProject, ...prev]);
        return newProject;
      } catch (dbError) {
        console.error('ProjectProvider - Database save failed, using localStorage:', dbError);
        
        // Fallback to localStorage if database fails
        const localProject = {
          ...projectData,
          id: Math.max(...projects.map(p => p.id || 0), 0) + 1,
          dateCreated: new Date().toISOString().split('T')[0],
          lastModified: new Date().toISOString().split('T')[0],
          linkedClasses: 0
        };
        
        // Add to state
        setProjects(prev => [localProject, ...prev]);
        
        // Save to localStorage
        const updatedProjects = [localProject, ...projects];
        localStorage.setItem('classtraks_projects', JSON.stringify(updatedProjects));
        
        return localProject;
      }
    } catch (error) {
      console.error('ProjectProvider - Error creating project:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update existing project
  const updateProject = async (id, updates) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ProjectProvider - Updating project:', id, updates);
      
      // Try database first
      try {
        const updatedProject = await projectsService.updateProject(id, updates);
        console.log('ProjectProvider - Project updated in database:', updatedProject);
        
        // Update local state
        setProjects(prev => prev.map(project => 
          project.id === id ? updatedProject : project
        ));
        
        return updatedProject;
      } catch (dbError) {
        console.error('ProjectProvider - Database update failed, using localStorage:', dbError);
        
        // Fallback to localStorage
        const updatedProject = {
          ...projects.find(p => p.id === id || p.id === parseInt(id)),
          ...updates,
          id: id,
          lastModified: new Date().toISOString().split('T')[0]
        };
        
        // Update state
        setProjects(prev => prev.map(project => 
          (project.id === id || project.id === parseInt(id)) ? updatedProject : project
        ));
        
        // Save to localStorage
        localStorage.setItem('classtraks_projects', JSON.stringify(projects));
        
        return updatedProject;
      }
    } catch (error) {
      console.error('ProjectProvider - Error updating project:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete project
  const deleteProject = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ProjectProvider - Deleting project:', id);
      
      // Try database first
      try {
        await projectsService.deleteProject(id);
        console.log('ProjectProvider - Project deleted from database');
      } catch (dbError) {
        console.error('ProjectProvider - Database delete failed:', dbError);
        // Continue with local deletion even if database fails
      }
      
      // Remove from local state (works for both DB and localStorage projects)
      setProjects(prev => prev.filter(project => 
        project.id !== id && project.id !== parseInt(id)
      ));
      
      // Update localStorage
      const updatedProjects = projects.filter(project => 
        project.id !== id && project.id !== parseInt(id)
      );
      localStorage.setItem('classtraks_projects', JSON.stringify(updatedProjects));
      
      return true;
    } catch (error) {
      console.error('ProjectProvider - Error deleting project:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete multiple projects
  const deleteProjects = async (ids) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ProjectProvider - Deleting multiple projects:', ids);
      
      // Try database deletions
      for (const id of ids) {
        try {
          await projectsService.deleteProject(id);
        } catch (dbError) {
          console.error(`ProjectProvider - Failed to delete project ${id} from database:`, dbError);
          // Continue with other deletions
        }
      }
      
      // Remove from local state
      setProjects(prev => prev.filter(project => 
        !ids.includes(project.id) && !ids.includes(parseInt(project.id))
      ));
      
      // Update localStorage
      const updatedProjects = projects.filter(project => 
        !ids.includes(project.id) && !ids.includes(parseInt(project.id))
      );
      localStorage.setItem('classtraks_projects', JSON.stringify(updatedProjects));
      
      return true;
    } catch (error) {
      console.error('ProjectProvider - Error deleting projects:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Clone project
  const cloneProject = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ProjectProvider - Cloning project:', id);
      
      // Find the project to clone
      const projectToClone = projects.find(p => 
        p.id === id || p.id === parseInt(id)
      );
      
      if (!projectToClone) {
        throw new Error('Project not found');
      }
      
      // Try database clone first
      try {
        const clonedProject = await projectsService.cloneProject(id);
        console.log('ProjectProvider - Project cloned in database:', clonedProject);
        
        // Add to local state
        setProjects(prev => [clonedProject, ...prev]);
        return clonedProject;
      } catch (dbError) {
        console.error('ProjectProvider - Database clone failed, using localStorage:', dbError);
        
        // Fallback to localStorage clone
        const clonedProject = {
          ...projectToClone,
          id: Math.max(...projects.map(p => p.id || 0), 0) + 1,
          name: `${projectToClone.name} (Copy)`,
          linkedClasses: 0,
          dateCreated: new Date().toISOString().split('T')[0],
          lastModified: new Date().toISOString().split('T')[0]
        };
        
        // Add to state
        setProjects(prev => [clonedProject, ...prev]);
        
        // Save to localStorage
        const updatedProjects = [clonedProject, ...projects];
        localStorage.setItem('classtraks_projects', JSON.stringify(updatedProjects));
        
        return clonedProject;
      }
    } catch (error) {
      console.error('ProjectProvider - Error cloning project:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Clone multiple projects
  const cloneProjects = async (ids) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ProjectProvider - Cloning multiple projects:', ids);
      
      const clonedProjects = [];
      
      for (const id of ids) {
        try {
          const clonedProject = await cloneProject(id);
          clonedProjects.push(clonedProject);
        } catch (error) {
          console.error(`ProjectProvider - Failed to clone project ${id}:`, error);
        }
      }
      
      return clonedProjects;
    } catch (error) {
      console.error('ProjectProvider - Error cloning projects:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get single project by ID (handles both integer and UUID)
  const getProject = (id) => {
    console.log('ProjectProvider - getProject called with ID:', id, typeof id);
    console.log('ProjectProvider - Available projects:', projects);
    
    const project = projects.find(project => {
      // Handle both string and number IDs for backwards compatibility
      const match = project.id === id || 
                   project.id === parseInt(id) || 
                   String(project.id) === String(id);
      
      if (match) {
        console.log('ProjectProvider - Found matching project:', project);
      }
      
      return match;
    });
    
    console.log('ProjectProvider - getProject result:', project);
    return project;
  };

  // Refresh projects from database
  const refreshProjects = () => {
    return fetchProjects();
  };

  const value = {
    projects,
    loading,
    error,
    fetchProjects,
    addProject,
    updateProject,
    deleteProject,
    deleteProjects,
    cloneProject,
    cloneProjects,
    getProject,
    refreshProjects
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};